import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Languages } from 'lucide-react'
import { logout, clearTokens, getTokens } from '../api/auth'
import packageJson from '../../package.json'

export default function Layout() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const handleLogout = async () => {
    const tokens = getTokens()

    if (tokens) {
      try {
        await logout(tokens.access_token)
      } catch (error) {
        console.warn('Logout request failed:', error)
      }
    }

    // Always clear tokens (client-side logout)
    clearTokens()

    // Redirect to login
    navigate('/login')
  }

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 overflow-x-hidden max-w-full">
      {/* Header */}
      <header className="bg-slate-900/70 backdrop-blur border-b border-slate-800 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between min-h-16 flex-wrap gap-2 w-full">
            <div className="flex items-center">
              <h1 className="text-2xl font-semibold text-primary-200 tracking-tight">
                {t('layout.title')}
              </h1>
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-2">
              <nav className="flex space-x-2">
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                    isActive
                      ? 'border-primary-500/60 bg-primary-500/10 text-primary-100 shadow-inner'
                      : 'border-transparent text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`
                }
              >
                {t('layout.dashboard')}
              </NavLink>
              <NavLink
                to="/conversations"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                    isActive
                      ? 'border-primary-500/60 bg-primary-500/10 text-primary-100 shadow-inner'
                      : 'border-transparent text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`
                }
              >
                {t('layout.conversations')}
              </NavLink>
              <NavLink
                to="/workflows"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                    isActive
                      ? 'border-primary-500/60 bg-primary-500/10 text-primary-100 shadow-inner'
                      : 'border-transparent text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`
                }
              >
                {t('layout.workflows')}
              </NavLink>
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                    isActive
                      ? 'border-primary-500/60 bg-primary-500/10 text-primary-100 shadow-inner'
                      : 'border-transparent text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`
                }
              >
                {t('layout.settings')}
              </NavLink>
              <NavLink
                to="/chat"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                    isActive
                      ? 'border-primary-500/60 bg-primary-500/10 text-primary-100 shadow-inner'
                      : 'border-transparent text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`
                }
              >
                {t('layout.chatTest')}
              </NavLink>
              </nav>

              {/* Language Switcher */}
              <div className="relative group">
                <button className="px-3 py-2 rounded-lg text-sm font-medium transition-all border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800/70 flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  {i18n.language.toUpperCase()}
                </button>
                <div className="absolute right-0 mt-2 w-32 bg-surface-200 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100]">
                  <button
                    onClick={() => changeLanguage('uk')}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-slate-800/70 first:rounded-t-lg ${
                      i18n.language === 'uk' ? 'bg-primary-500/10 text-primary-200' : 'text-slate-300'
                    }`}
                  >
                    UA Українська
                  </button>
                  <button
                    onClick={() => changeLanguage('ru')}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-slate-800/70 ${
                      i18n.language === 'ru' ? 'bg-primary-500/10 text-primary-200' : 'text-slate-300'
                    }`}
                  >
                    RU Русский
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-slate-800/70 last:rounded-b-lg ${
                      i18n.language === 'en' ? 'bg-primary-500/10 text-primary-200' : 'text-slate-300'
                    }`}
                  >
                    EN English
                  </button>
                </div>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="ml-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
              >
                {t('layout.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 overflow-x-hidden">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900/70 backdrop-blur border-t border-slate-800 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-slate-400">
            {t('layout.footer', { version: packageJson.version })}
          </p>
        </div>
      </footer>
    </div>
  )
}
